// Master Intake schema — extracted from CF Full Circle Master Intake & Attorney Review workbook
// and the First-Client Launch Package "Master Client Intake Form" (client-facing sections only).
export const INTAKE_SECTIONS = [
  {
    id: "identity",
    title: "Your Identity & Contact",
    intro: "Exactly as shown on your official documents.",
    fields: [
      { key: "full_legal_name", label: "Full legal name", type: "text", required: true },
      { key: "other_names", label: "Other names / maiden names / aliases", type: "text" },
      { key: "dob", label: "Date of birth", type: "date" },
      { key: "country_city_birth", label: "Country & city of birth", type: "text" },
      { key: "citizenship", label: "Country/countries of citizenship", type: "text" },
      { key: "a_number", label: "A-Number (if any)", type: "text" },
      { key: "uscis_account", label: "USCIS Online Account Number (if any)", type: "text" },
      { key: "ssn_itin", label: "SSN / ITIN (if any)", type: "text" },
      { key: "passport_number", label: "Passport number", type: "text" },
      { key: "passport_country", label: "Passport country", type: "text" },
      { key: "passport_expiration", label: "Passport expiration", type: "date" },
      { key: "current_address", label: "Current U.S. address", type: "textarea" },
      { key: "mailing_address", label: "Mailing address (if different)", type: "textarea" },
      { key: "phone", label: "Phone", type: "text" },
      { key: "email", label: "Email", type: "text" },
      { key: "preferred_contact", label: "Preferred contact method", type: "select", options: ["Secure portal", "Email", "SMS / text", "Phone call", "Mail"] },
      { key: "safe_voicemail", label: "Safe to leave a voicemail?", type: "yesno" },
      { key: "preferred_language", label: "Preferred language", type: "text" },
      { key: "interpreter_needed", label: "Interpreter needed? If yes, which language?", type: "text" },
      { key: "marital_status", label: "Current marital status", type: "select", options: ["Single", "Married", "Separated", "Divorced", "Widowed"] },
    ],
  },
  {
    id: "triage",
    title: "Immediate Triage",
    intro: "Check anything that applies — this helps us act fast on urgent matters.",
    fields: [
      {
        key: "triage_flags", label: "Do any of these apply to you right now?", type: "checks", options: [
          "Currently detained", "Immigration court hearing scheduled", "BIA / appeal deadline",
          "USCIS interview scheduled", "RFE / NOID / denial received", "Removal or deportation order known or suspected",
          "Arrest / criminal charge / history", "Domestic violence / trafficking / victimization concern",
          "Passport or ID expiring soon", "Urgent travel need", "Other filing deadline",
        ],
      },
      { key: "triage_explain", label: "Please explain any checked item", type: "textarea" },
    ],
  },
  {
    id: "immigration_history",
    title: "Immigration History",
    fields: [
      { key: "first_entry_date", label: "Date of first U.S. entry", type: "date" },
      { key: "recent_entry_date", label: "Most recent U.S. entry", type: "date" },
      { key: "place_of_entry", label: "Place of entry", type: "text" },
      { key: "class_of_admission", label: "Status / class of admission (e.g. B-2 visitor)", type: "text" },
      { key: "i94_expiration", label: "I-94 expiration or D/S", type: "text" },
      { key: "current_status", label: "Current immigration status, if known", type: "text" },
      { key: "prior_visa_apps", label: "Prior visa applications or denials", type: "textarea" },
      { key: "prior_petitions", label: "Prior immigration petitions or applications", type: "textarea" },
      { key: "prior_removal", label: "Prior removal / deportation proceedings", type: "textarea" },
      { key: "prior_asylum", label: "Prior asylum application", type: "textarea" },
      { key: "prior_unauthorized_employment", label: "Prior unauthorized employment", type: "textarea" },
      { key: "prior_overstays", label: "Prior overstays or status violations", type: "textarea" },
      {
        key: "entry_flags", label: "Check any that apply to your U.S. entries", type: "checks", options: [
          "Ever entered without inspection", "Ever refused entry or withdrew application", "Secondary inspection",
          "Expedited removal", "Paroled into the U.S.", "Used another person's document",
          "Used a different name or date of birth", "Claimed U.S. citizenship",
          "Had immigrant petition pending when seeking nonimmigrant entry",
        ],
      },
      { key: "entry_details", label: "Details about any checked entry item", type: "textarea" },
    ],
  },
  {
    id: "prior_filings",
    title: "Prior Immigration Filings",
    fields: [
      {
        key: "prior_forms", label: "Forms or matters previously filed (check all that apply)", type: "checks", options: [
          "I-130", "I-140", "I-360", "I-485", "I-589", "I-765", "I-90", "N-400",
          "I-601 / I-601A", "I-212", "I-918 (U visa)", "I-914 (T visa)", "TPS", "DACA",
          "DS-160 / DS-260", "EOIR filing / appeal / motion", "Other",
        ],
      },
      { key: "prior_filings_details", label: "Prior denials, RFEs, NOIDs, revocations or withdrawals (forms, dates, receipt numbers)", type: "textarea" },
    ],
  },
  {
    id: "family",
    title: "Family & Relationships",
    fields: [
      { key: "spouse", label: "Current spouse — name and immigration status", type: "textarea" },
      { key: "prior_marriages", label: "Prior marriages and termination dates", type: "textarea" },
      { key: "children", label: "Children — names, dates of birth, birth countries, status", type: "textarea" },
      { key: "parents", label: "Parents — names, citizenship / status", type: "textarea" },
      { key: "usc_lpr_relatives", label: "U.S. citizen or permanent resident relatives", type: "textarea" },
      { key: "petitioner", label: "Petitioner / sponsor, if any", type: "text" },
    ],
  },
  {
    id: "court",
    title: "Immigration Court & ICE Screening",
    intro: "Answer what you know — it is fine to leave items blank.",
    fields: [
      {
        key: "court_flags", label: "Check any that apply", type: "checks", options: [
          "Ever received a Notice to Appear (NTA)", "Prior removal / deportation / exclusion order",
          "In absentia order", "Voluntary departure", "Expedited removal", "Reinstatement of removal",
          "ICE check-ins / order of supervision", "Bond or detention history", "Appeal or motion history",
        ],
      },
      { key: "eoir_a_number", label: "EOIR A-Number", type: "text" },
      { key: "immigration_court", label: "Immigration court (city)", type: "text" },
      { key: "next_hearing", label: "Next hearing date", type: "date" },
      { key: "judge_name", label: "Judge (if known)", type: "text" },
      { key: "court_details", label: "Court / ICE details (attach notices in Documents)", type: "textarea" },
    ],
  },
  {
    id: "criminal",
    title: "Criminal & Security Screening",
    intro: "Answering YES does not automatically mean you are ineligible. Honest answers help us determine whether attorney review is required.",
    fields: [
      { key: "q_arrested", label: "Ever arrested, cited, charged, convicted, or placed on probation?", type: "yesno" },
      { key: "q_failed_appear", label: "Ever failed to appear in court?", type: "yesno" },
      { key: "q_claimed_usc", label: "Ever claimed to be a U.S. citizen?", type: "yesno" },
      { key: "q_voted", label: "Ever voted or registered to vote in the U.S.?", type: "yesno" },
      { key: "q_false_documents", label: "Ever used false documents or another person's identity?", type: "yesno" },
      { key: "q_false_info", label: "Ever given false information to an immigration or consular officer?", type: "yesno" },
      { key: "q_smuggling", label: "Ever helped someone enter the U.S. unlawfully?", type: "yesno" },
      { key: "q_ordered_removed", label: "Ever been ordered removed/deported or granted voluntary departure?", type: "yesno" },
      { key: "q_worked_unauthorized", label: "Ever worked without authorization?", type: "yesno" },
      { key: "q_overstayed", label: "Ever remained after your authorized stay expired?", type: "yesno" },
      { key: "criminal_details", label: "Details for any YES answer (dates, places, outcomes)", type: "textarea" },
    ],
  },
  {
    id: "safety",
    title: "Safety & Humanitarian Screen (Confidential)",
    intro: "Conducted privately and sensitively. Share only what you are comfortable sharing at this stage.",
    fields: [
      {
        key: "safety_flags", label: "Check any that apply", type: "checks", options: [
          "Afraid to return to home country", "Threatened, harmed, or persecuted",
          "Domestic violence or extreme cruelty", "Victim of a serious crime in the U.S.",
          "Helped or willing to help law enforcement", "Labor exploitation or trafficking indicators",
          "Abuse by U.S. citizen / LPR spouse or parent", "Child safety concerns",
          "Government unable or unwilling to protect", "Prior residence or status in a third country",
        ],
      },
      { key: "safety_details", label: "Anything you would like us to know (kept confidential)", type: "textarea" },
    ],
  },
  {
    id: "needs",
    title: "What You Need Help With",
    intro: "Your Full Circle needs assessment — check everything that applies, not just immigration.",
    fields: [
      {
        key: "needs", label: "Services you may need", type: "checks", options: [
          "Immigration representation", "Home-country birth certificate", "Marriage certificate",
          "Death certificate", "Divorce record", "Police certificate", "School transcript / diploma",
          "Passport renewal / replacement", "Foreign national ID", "Foreign driver's license assistance",
          "Consular appointment", "Passport photos", "Visa photos", "Notary",
          "Apostille / authentication / legalization", "Certified translation coordination",
          "ITIN / W-7", "Tax preparation / referral", "Bank account guidance", "Credit education",
          "Background check", "MVR", "DOT / non-DOT testing", "Fingerprinting",
          "Health-insurance navigation", "Housing assistance", "Transportation assistance",
          "School / daycare enrollment", "Employment / workforce assistance",
          "Professional credential evaluation", "Other",
        ],
      },
      { key: "needs_other", label: "Other needs or notes", type: "textarea" },
    ],
  },
  {
    id: "goals",
    title: "Your Goals",
    fields: [
      { key: "goal", label: "What are you trying to accomplish?", type: "textarea" },
      { key: "biggest_concern", label: "What is your biggest concern right now?", type: "textarea" },
      { key: "important_dates", label: "Important dates or deadlines", type: "textarea" },
    ],
  },
  {
    id: "documents",
    title: "Documents You Already Have",
    fields: [
      {
        key: "documents_have", label: "Check all that you currently have", type: "checks", options: [
          "Passport", "Visa", "I-94", "Green card / EAD", "Birth certificate",
          "Marriage / divorce records", "Prior immigration filings", "USCIS notices",
          "Immigration court notices", "Police / court records", "Tax returns", "School records", "Other",
        ],
      },
      { key: "missing_docs", label: "Documents you are missing or need help retrieving", type: "textarea" },
    ],
  },
  {
    id: "attestation",
    title: "Attestation & Signature",
    intro: "I understand that accurate and complete information is important. I have had an opportunity to correct the factual information I provided during intake, and I understand that additional facts or documents may be required.",
    fields: [
      { key: "attest", label: "I confirm the information I provided is true and complete to the best of my knowledge.", type: "check" },
      { key: "signature_name", label: "Type your full name to sign", type: "text" },
      { key: "interpreter_name", label: "Interpreter name (if one helped you)", type: "text" },
    ],
  },
];

export const INTAKE_FIELD_LABELS = INTAKE_SECTIONS.flatMap((s) => s.fields.map((f) => [f.key, f.label]));
